import React, { Component } from 'react'
import { injectState } from 'freactal'
import ImageCropper from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { getAuthenticatedImage } from '../../lib'
import app from '../../feathers'

import InitialScreen from './InitialScreen'
import LibraryScreen from './LibraryScreen'
import UnitScreen from './UnitScreen'
import UploadScreen from './UploadScreen'
import TagImagesScreen from './TagImagesScreen'
import EditImagesScreen from './EditImagesScreen'

const imagesService = app.service('images')
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg']

class ImageSelectionModal extends Component {
  state = {
    panel: 'initial',
    currentImageIndex: 0,
    searchTerm: '',
    imageSuggestions: null,
  }
  async componentWillMount() {
    const { scopeContent } = this.props
    const { data: imageSuggestions } = await imagesService.find({
      query: {
        $limit: 4,
        $search: scopeContent,
      },
    })
    this.setState({
      imageSuggestions,
    })
  }
  limitReachedAlert() {
    alert(
      `Only able to insert ${this.props.imageLimit} image${this.props
        .imageLimit > 1
        ? 's'
        : ''}.`,
    )
  }
  goToPanel = panel => () => this.setState({ panel })
  goToNextImage = () =>
    this.setState(({ currentImageIndex }) => ({
      currentImageIndex: currentImageIndex + 1,
    }))

  handleSearchChange = searchTerm => {
    this.setState({ searchTerm })
  }

  handleSearch = async () => {
    const { searchTerm } = this.state
    this.setState({
      searchingLibrary: true,
    })
    if (searchTerm !== '') {
      const { data: libraryImages } = await imagesService.find({
        query: {
          $limit: 50,
          $search: searchTerm,
        },
      })
      this.setState({
        libraryImages,
        searchingLibrary: false,
      })
    } else {
      const { data: libraryImages } = await imagesService.find({})
      this.setState({ libraryImages, searchingLibrary: false })
    }
  }
  handleChangeImage = (change, additionalState = {}) =>
    this.setState(({ images, currentImageIndex }) => ({
      ...additionalState,
      images: images.map((image, i) => {
        if (currentImageIndex !== i) return image
        return {
          ...image,
          ...change,
        }
      }),
    }))
  handleImageCrop = (cropView, cropRect) => {
    this.handleChangeImage({
      cropRect,
      cropView,
    })
  }
  handleChangeImageDescription = ({ target }) =>
    this.handleChangeImage({ description: target.value })
  handleChangeImageTags = ({ target }) =>
    this.handleChangeImage({ tags: target.value.split(', ') })
  handleChangeImageRotation = rotation => this.handleChangeImage({ rotation })
  handleChangeImageMirror = mirror => this.handleChangeImage({ mirror })
  handleImageDrop = images => {
    if (!images.every(({ type }) => ACCEPTED_IMAGE_TYPES.includes(type))) {
      return alert('Image format not accepted.')
    }
    if (images.length > this.props.imageLimit) {
      return this.limitReachedAlert()
    }
    this.setState({
      images: images.map(file => ({
        file,
        cropView: { ...ImageCropper.defaultCrop },
        description: '',
        tags: [],
      })),
    })
  }
  handleImageUpload = async () => {
    const responses = await Promise.all(
      this.state.images.map(async ({ file, tags, description }) => {
        const body = new FormData()
        body.append('image', file)
        body.append('tags', tags)
        body.append('description', description)
        const token = await app.passport.getJWT()
        const response = await fetch('/images/', {
          method: 'POST',
          body,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        if (!response.ok) {
          return alert(`Failed to upload image. ${await response.text()}`)
        }
        return response.json()
      }),
    )
    const mediaIds = responses.map(({ imageId }) => imageId)
    this.setState({
      images: this.state.images.map((image, i) => ({
        ...image,
        mediaId: mediaIds[i],
      })),
      panel: 'edit-images',
      currentImageIndex: 0,
    })
  }
  handleHandleImageSave = async () => {
    const images = this.state.images.map(image => ({
      mediaId: image.mediaId,
      transforms: {
        crop: image.cropRect,
        rotation: image.rotation,
        mirror: image.mirror,
      },
    }))
    this.props.callback(images)
  }
  handleImageSelect = image => async () => {
    const { _id: mediaId } = image
    const token = await app.passport.getJWT()
    const authenticatedSource = await getAuthenticatedImage(
      `/images/${mediaId}`,
      token,
    )
    this.setState(({ images = [] }) => {
      images = images.some(({ _id }) => _id === image._id)
        ? images.filter(({ _id }) => _id !== image._id)
        : images.concat({
            ...image,
            authenticatedSource,
            mediaId,
            cropView: { ...ImageCropper.defaultCrop },
          })
      if (images.length > this.props.imageLimit) {
        return this.limitReachedAlert()
      }
      return {
        images,
      }
    })
  }
  getImageSrc = image => {
    if (image.file) {
      return image.file.preview
    }
    if (image.authenticatedSource) {
      return image.authenticatedSource
    }
    return `/images/${image.mediaId}`
  }
  render() {
    const { unitImages = [] } = this.props
    const {
      images = [],
      panel,
      currentImageIndex,
      imageSuggestions,
      searchTerm,
      searchingLibrary,
      libraryImages,
    } = this.state
    const imageIds = images.map(({ mediaId }) => mediaId)

    switch (panel) {
      case 'initial':
      default: {
        return (
          <InitialScreen
            images={images}
            imageIds={imageIds}
            imageSuggestions={imageSuggestions}
            unitImages={unitImages}
            goToPanel={this.goToPanel}
            handleImageSelect={this.handleImageSelect}
          />
        )
      }
      case 'library': {
        const imageIds = images.map(({ mediaId }) => mediaId)
        return (
          <LibraryScreen
            searchTerm={searchTerm}
            libraryImages={libraryImages}
            searchingLibrary={searchingLibrary}
            imageIds={imageIds}
            images={images}
            goToPanel={this.goToPanel}
            handleSearch={this.handleSearch}
            handleSearchChange={this.handleSearchChange}
            handleImageSelect={this.handleImageSelect}
          />
        )
      }
      case 'unit': {
        const { images = [] } = this.state
        const { unitImages = [] } = this.props
        const imageIds = images.map(({ mediaId }) => mediaId)
        return (
          <UnitScreen
            unitImages={unitImages}
            images={images}
            imageIds={imageIds}
            handleImageSelect={this.handleImageSelect}
            goToPanel={this.goToPanel}
          />
        )
      }
      case 'upload':
        return (
          <UploadScreen
            images={images}
            goToPanel={this.goToPanel}
            handleImageDrop={this.handleImageDrop}
          />
        )
      case 'tag-images': {
        const { images, currentImageIndex } = this.state
        return (
          <TagImagesScreen
            images={images}
            currentImageIndex={currentImageIndex}
            handleChangeImageDescription={this.handleChangeImageDescription}
            handleChangeImageTags={this.handleChangeImageTags}
            handleImageUpload={this.handleImageUpload}
            goToNextImage={this.goToNextImage}
          />
        )
      }
      case 'edit-images': {
        return (
          <EditImagesScreen
            images={images}
            currentImageIndex={currentImageIndex}
            handleChangeImageRotation={this.handleChangeImageRotation}
            handleChangeImageMirror={this.handleChangeImageMirror}
            getImageSrc={this.getImageSrc}
            handleImageCrop={this.handleImageCrop}
            handleHandleImageSave={this.handleHandleImageSave}
            goToNextImage={this.goToNextImage}
          />
        )
      }
    }
  }
}

export default injectState(ImageSelectionModal)



// WEBPACK FOOTER //
// ./src/components/ImageSelectionModal/index.js